import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { includes } from 'lodash';
import { Observable, ReplaySubject, firstValueFrom, map, tap, catchError, of } from 'rxjs';
import { WINDOW } from '../core';
import { IntercomService } from '../intercom';
import { IUser, User } from './user.type';
import { FeedItem } from "../../../header/header.type";
import { LoggerService } from "@teammood/shared/util/logger";

@Injectable({
    providedIn: 'root'
})
export class UserService {

    private currentUser: User;
    private currentUser$: ReplaySubject<User>;

    constructor(
        private http: HttpClient,
        @Inject(WINDOW) private window: Window & { trackJs: any },
        private intercomService: IntercomService,
        private logger: LoggerService
    ) {
    }
    
    private _fetch(): Observable<User> {
        return this.http.get<IUser>('/api/user').pipe(
            map(user => this._mapToUser(user)),
            tap(user => this.set(user)),
            catchError(err => {
                if (err.status !== 401) {
                    this.logger.error('error getting user', err);
                }
                return of(null);
            }),
        );
    }

    private _mapToUser(user: IUser): User {
        return {
            ...user,
            id: user.teamMemberId,
            isAdmin: user.roles && (includes(user.roles, "manager") || includes(user.roles, "admin") || includes(user.roles, "observer"))
        }
    }

    set(user: IUser) {
        this.currentUser = this._mapToUser(user);

        if (!this.currentUser$) {
            this.currentUser$ = new ReplaySubject<User>(1);
        }

        this.currentUser$.next(this.currentUser);

        this.intercomService.boot(this.currentUser);
        
        if (this.window.trackJs) {
            this.window.trackJs.configure({userId: user.teamMemberId});
        }
    }

    get(fetch = false): Promise<User> {

        const shouldFetch = fetch || !this.currentUser$;

        if (!this.currentUser$) {
            this.currentUser$ = new ReplaySubject<User>(1);
        }

        if (shouldFetch) {
            return firstValueFrom(this._fetch());
        } else {
            return firstValueFrom(this.currentUser$);
        }
    }

    getCurrentUser(): Observable<User> {
        return this.currentUser$.asObservable();
    }


    async isAdminOfMultipleTeams() {
        const currentUser = await this.get();
        const teams = currentUser.teams;
        if(teams && teams.length > 1){
            const adminTeams = teams.filter(team => team.roles.indexOf('admin')!=-1);
            return adminTeams.length > 1;
        }else{
            return false;
        }
    }

    async getFeed(): Promise<FeedItem[]> {
        return await firstValueFrom(this.http.get<FeedItem[]>('/api/feed'));
    }


    clear() {
        this.currentUser = null;
        this.currentUser$ = null;
    }
}
